import { Button } from "@components/atoms/Button"
import { Logo } from "@components/atoms/Logo"
import { Menu } from "@components/atoms/Menu"
import { cn } from "@utils/tw"
import { useAppData } from "eddev/hooks"
import { Link, useRouter } from "eddev/routing"
import { MenuLocationEnum } from "../../types.graphql"
import { useState } from "react"

export function Header({ style }: { style?: string | null }) {
  const menus = useAppData((appData) => appData.menus?.nodes)
  const mainMenu = menus?.find((menu) => menu.locations?.includes(MenuLocationEnum.Main))
  const theme = style === "light" ? "light" : "dark"
  const [menuOpen, setMenuOpen] = useState(false)

  return (
    <>
      <div
        className={cn(
          "absolute left-0 top-0 w-full z-[9999]",
          theme === "dark" && "text-black",
          theme === "light" && "text-white",
        )}
      >
        <div className="w-grid py-6 mx-auto flex items-center justify-between relative">
          <Link href="/">
            <Logo />
          </Link>

          <div className="flex items-center gap-10">
            <div className="gap-3 items-center hidden md:flex">
              {mainMenu?.menuItems?.nodes.map((item) => (
                <Link
                  key={item.url}
                  href={item.url!}
                  target={item.target!}
                  className={cn(
                    "type-desktop-label-label-m px-4 py-2 transition-colors duration-300",
                    theme === "dark" &&
                      "hover:bg-black hover:text-white data-[active=exact]:bg-black data-[active=exact]:text-white data-[child-active=true]:bg-black data-[child-active=true]:text-white",
                    theme === "light" &&
                      "hover:bg-white hover:text-black data-[active=exact]:bg-white data-[active=exact]:text-black data-[child-active=true]:bg-white data-[child-active=true]:text-black",
                  )}
                >
                  {item.label}
                </Link>
              ))}
            </div>

            <div className="flex gap-8 md:gap-4">
              {/* <Link href="/">
                <LanguageIcon className="w-[1em] md:w-[1em] h-[1em] md:h-[1em]" />
              </Link> */}
              <Link href="/search">
                <SearchIcon className="w-[1em] md:w-[1em] h-[1em] md:h-[1em]" />
              </Link>
              <button
                className="md:hidden flex appearance-none border-none bg-none"
                onClick={() => setMenuOpen(!menuOpen)}
              >
                <MenuIcon className="w-[1em] md:w-[1em] h-[1em] md:h-[1em]" />
              </button>
            </div>
          </div>
        </div>
      </div>
      <MobileMenu open={menuOpen} setOpen={setMenuOpen} />
    </>
  )
}

const MobileMenu = ({ open, setOpen }: { open: boolean; setOpen: (open: boolean) => void }) => {
  const menus = useAppData((appData) => appData.menus?.nodes)
  const mainMenu = menus?.find((menu) => menu.locations?.includes(MenuLocationEnum.Main))

  return (
    <div
      className={cn(
        "md:hidden fixed top-0 left-0 w-full h-full bg-white z-[9999] pointer-events-none transition-opacity opacity-0",
        open && "opacity-100 pointer-events-auto",
      )}
    >
      <div className="flex flex-col items-center justify-center gap-3 mt-6 w-full h-full absolute left-0 top-0">
        {mainMenu?.menuItems?.nodes.map((item) => (
          <Link
            key={item.url}
            href={item.url!}
            target={item.target!}
            className={cn(
              "type-mobile-headings-heading-l px-4 py-2 transition-colors duration-300",
              "hover:bg-black hover:text-white data-[active=exact]:bg-black data-[active=exact]:text-white data-[child-active=true]:bg-black data-[child-active=true]:text-white",
            )}
          >
            {item.label}
          </Link>
        ))}
      </div>

      <div className="w-grid py-6 mx-auto flex items-center justify-between relative">
        <Link href="/">
          <Logo />
        </Link>

        <button className="flex appearance-none border-none bg-none" onClick={() => setOpen(false)}>
          <MenuIcon className="w-[1em] md:w-[1em] h-[1em] md:h-[1em]" />
        </button>
      </div>
    </div>
  )
}

const LanguageIcon = ({ className }: { className?: string }) => {
  return (
    <svg className={cn("w-full h-full", className)} viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M20.5 10.4596C20.5007 11.7775 20.2418 13.0825 19.7383 14.3003C19.2347 15.5181 18.4963 16.6247 17.5651 17.5571C16.634 18.4894 15.5283 19.2292 14.3113 19.7341C13.0944 20.2391 11.7899 20.4993 10.4723 20.5C7.79873 20.4556 5.25088 19.3568 3.38329 17.4426C1.51569 15.5285 0.479531 12.9541 0.500306 10.2796C0.502447 7.72318 1.50512 5.2692 3.29366 3.44302C5.08221 1.61685 7.51447 0.563626 10.0697 0.508844C10.2141 0.502756 10.3584 0.500146 10.5045 0.500146C11.8159 0.493031 13.1157 0.745504 14.3292 1.24302C15.5426 1.74054 16.6456 2.47327 17.5747 3.39901C18.5038 4.32475 19.2406 5.4252 19.7426 6.63698C20.2447 7.84876 20.5021 9.14792 20.5 10.4596Z"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10.0697 0.50885C4.85241 6.16221 4.85241 13.5377 10.0697 20.4957"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10.9392 0.50885C16.1565 6.16221 16.1565 13.5351 10.9392 20.493"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M2.14038 4.84367H18.7784" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M0.50293 10.0622H20.5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M1.79175 15.2806H19.2697" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

const SearchIcon = ({ className }: { className?: string }) => {
  return (
    <svg className={cn("w-full h-full", className)} viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M11.3182 11.3182L14.5 14.5M0.5 6.86364C0.5 8.5514 1.17045 10.17 2.36386 11.3634C3.55728 12.5568 5.1759 13.2273 6.86364 13.2273C8.5514 13.2273 10.17 12.5568 11.3634 11.3634C12.5568 10.17 13.2273 8.5514 13.2273 6.86364C13.2273 5.1759 12.5568 3.55728 11.3634 2.36386C10.17 1.17045 8.5514 0.5 6.86364 0.5C5.1759 0.5 3.55728 1.17045 2.36386 2.36386C1.17045 3.55728 0.5 5.1759 0.5 6.86364Z"
        stroke="currentColor"
      />
    </svg>
  )
}

const MenuIcon = ({ className }: { className?: string }) => {
  return (
    <svg className={cn("w-full h-full", className)} viewBox="0 0 12 10" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M11.666 10H0V8.33301H11.666V10ZM11.666 5.83301H0V4.16699H11.666V5.83301ZM11.666 1.66699H0V0H11.666V1.66699Z"
        fill="currentColor"
      />
    </svg>
  )
}
